/**
 * ImmersiveWorkoutControls - Main action button
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '../../../../Theme/Theme';
import { ViewSet } from '../../../../Model/WorkoutLog/ViewSet';
import { useTrans } from '../../../../Store/System/LocalizationStore';

const Container = styled.div`
  padding: 16px 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  display: flex;
  justify-content: center;
`;

const MainButton = styled.button<{ isStop?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 40px;
  border-radius: 32px;
  border: none;
  background: ${({ isStop }) => (isStop ? Theme.Colors.error : Theme.Colors.primary)};
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  min-width: 180px;
  box-shadow: ${({ isStop }) =>
    isStop ? `0 4px 12px ${Theme.Colors.error}66` : `0 4px 12px ${Theme.Colors.primary}66`};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }

  & > i {
    font-size: 24px;
  }
`;

export type ImmersiveWorkoutControlsProps = {
  viewSet: ViewSet;
  isLastExercise: boolean;
  onNext: () => void;
  onStop: () => void;
};

export const ImmersiveWorkoutControls: React.FC<ImmersiveWorkoutControlsProps> = observer(
  ({ viewSet, isLastExercise, onNext, onStop }) => {
    const { t } = useTrans();

    const isFinished = viewSet.isFinished;

    const handlePress = React.useCallback(() => {
      if (isLastExercise && isFinished) {
        onStop();
      } else {
        onNext();
      }
    }, [isFinished, isLastExercise, onStop, onNext]);

    const buttonLabel = React.useMemo(() => {
      if (isLastExercise && isFinished) {
        return t('workout.finish', { defaultValue: 'Finish' });
      }
      if (isFinished) {
        return t('workout.next', { defaultValue: 'Next' });
      }
      return t('workout.done', { defaultValue: 'Done' });
    }, [isFinished, isLastExercise, t]);

    const iconName = isLastExercise && isFinished ? 'mdi mdi-flag-checkered' : 'mdi mdi-check';

    return (
      <Container>
        <MainButton isStop={isLastExercise && isFinished} onClick={handlePress}>
          <i className={iconName} />
          {buttonLabel}
        </MainButton>
      </Container>
    );
  },
);
